import {
  Text,
  IconChip,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  IndigoIndigo50,
  Primary12Regular,
  Primary12SemiBold,
  RedRed80,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import { FC, useMemo } from 'react'
import { MetricGraph } from './MetricGraph'
import {
  MEMORY_ALERT_THRESHOLD_BYTES,
  MEMORY_GRAPH_UPPER_LIMIT,
  formatBytes,
  formatPercent
} from './performanceMonitorBar.constants'
import { MetricSample } from './performanceMonitorBar.types'
import { useMemoryUsage } from './useMemoryUsage'
import { VerticalGauge } from './VerticalGauge'

type MemoryItemProps = {
  isExpanded: boolean
}

const MEMORY_TOOLTIP_LABEL = 'JS heap memory'

export const MemoryItem: FC<MemoryItemProps> = ({ isExpanded }) => {
  const { memory, history } = useMemoryUsage()

  const isOverThreshold =
    memory !== null && memory.usedJSHeapSize > MEMORY_ALERT_THRESHOLD_BYTES

  const accentColor = isOverThreshold ? RedRed80 : IndigoIndigo50
  const valueColor = isOverThreshold ? RedRed80 : GreyGrey80
  const iconColor = isOverThreshold ? RedRed80 : GreyGrey80

  const percentUsed =
    memory !== null
      ? Math.min(100, (memory.usedJSHeapSize / MEMORY_GRAPH_UPPER_LIMIT) * 100)
      : 0

  const metricHistory = useMemo<MetricSample[]>(
    () =>
      history.map(sample => ({
        timestamp: sample.timestamp,
        value: sample.usedJSHeapSize
      })),
    [history]
  )

  if (!isExpanded) {
    return (
      <Flex flexDirection="column" alignItems="center" gap="6px" width="100%">
        <Tooltip label={MEMORY_TOOLTIP_LABEL} placement="right" hasArrow>
          <IconChip color={iconColor} />
        </Tooltip>
        <VerticalGauge
          percentUsed={percentUsed}
          hasValue={memory !== null}
          accentColor={accentColor}
          borderColor={GreyGrey30}
          tooltipLabel={
            memory !== null
              ? `${formatBytes(memory.usedJSHeapSize)} used`
              : undefined
          }
        />
        {memory !== null && (
          <Text {...Primary12SemiBold} color={valueColor}>
            {formatPercent(percentUsed)}
          </Text>
        )}
      </Flex>
    )
  }

  if (memory === null) {
    return (
      <Box paddingX="12px">
        <Text {...Primary12Regular} color={GreyGrey80}>
          performance.memory unavailable (Chromium only)
        </Text>
      </Box>
    )
  }

  return (
    <Flex flexDirection="column" gap="12px" paddingX="12px" width="100%">
      <Flex alignItems="center" gap="6px">
        <Tooltip label={MEMORY_TOOLTIP_LABEL} placement="right" hasArrow>
          <IconChip size={14} color={iconColor} />
        </Tooltip>
        <Text {...Primary12SemiBold} color={GreyGrey80}>
          Memory (JS heap)
        </Text>
      </Flex>
      <MetricGraph
        history={metricHistory}
        yMax={MEMORY_GRAPH_UPPER_LIMIT}
        threshold={null}
        accentColor={accentColor}
        borderColor={GreyGrey30}
        isOverThreshold={isOverThreshold}
        formatValue={formatBytes}
      />
      <Flex flexDirection="column" gap="4px">
        <Text {...Primary12Regular} color={GreyGrey60}>
          Used: {formatBytes(memory.usedJSHeapSize)} (
          {formatPercent(percentUsed)})
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60}>
          Allocated: {formatBytes(memory.totalJSHeapSize)}
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60}>
          Heap limit: {formatBytes(memory.jsHeapSizeLimit)}
        </Text>
      </Flex>
      {isOverThreshold && (
        <Text {...Primary12SemiBold} color={RedRed80}>
          ⚠ Heap above 512 MB
        </Text>
      )}
    </Flex>
  )
}
